import { useEffect, useMemo, useState } from 'react';
import { Modal } from 'antd';
import type { UploadProps } from 'antd';
import type { RcFile } from 'antd/es/upload';
import { useProfile } from '../../profile/components/ProfileContext';
import { UtilService } from '../../../../../core/services/util.service';
import { Company } from '../models/company-profile.model';
import { CompanyService } from '../services/company-profile.service';
import { CompanyMetrics } from './company-workspace.utils';
import CompanyEditor from './CompanyEditor';
import CompanyOverview from './CompanyOverview';

interface Props {
  company: Company;
  editing: boolean;
  logo: string;
  metrics: CompanyMetrics;
  onCompanyChange: (company: Company) => void;
  onDirtyChange: (dirty: boolean) => void;
  onEditingChange: (editing: boolean) => void;
  onLogoChange: (dataUrl: string) => void;
}

const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const validateLogo = (file: RcFile) => {
  const supported = file.type === 'image/jpeg' || file.type === 'image/png';
  const withinLimit = file.size / 1024 / 1024 < 2;

  if (!supported) {
    UtilService.Alert(
      'Unsupported image',
      'error',
      'Upload a PNG or JPG logo.'
    );
  } else if (!withinLimit) {
    UtilService.Alert(
      'Image is too large',
      'error',
      'Choose a logo smaller than 2 MB.'
    );
  }
  return supported && withinLimit;
};

const CompanyProfile = ({
  company,
  editing,
  logo,
  metrics,
  onCompanyChange,
  onDirtyChange,
  onEditingChange,
  onLogoChange,
}: Props) => {
  const { companyId, isAdmin, profile } = useProfile();
  const [draft, setDraft] = useState<Company>(company);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(company);
  }, [company, editing]);

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(company),
    [company, draft]
  );

  useEffect(() => {
    const protectChanges = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', protectChanges);
    return () => window.removeEventListener('beforeunload', protectChanges);
  }, [dirty]);

  const handleChange = (
    name: 'name' | 'website' | 'aboutUs',
    value: string
  ) => {
    const next = { ...draft, [name]: value };
    setDraft(next);
    onDirtyChange(JSON.stringify(next) !== JSON.stringify(company));
  };

  const handleSave = async () => {
    if (!draft.name.trim() || !draft.website.trim()) return;

    setSaving(true);
    try {
      if (draft.id) {
        await CompanyService.Patch(draft.id, draft);
        onCompanyChange(draft);
      } else {
        onCompanyChange(await CompanyService.Post(draft));
      }
      onEditingChange(false);
      UtilService.Alert(
        'Company updated',
        'success',
        'Your company profile changes are now live.'
      );
    } catch (error) {
      console.error('Error saving company profile:', error);
      UtilService.Alert(
        'Unable to save',
        'error',
        'Check your connection and try saving the company profile again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!dirty) {
      onEditingChange(false);
      return;
    }

    Modal.confirm({
      title: 'Discard unsaved changes?',
      content: 'Your company profile will return to its last saved version.',
      okText: 'Discard changes',
      cancelText: 'Keep editing',
      okButtonProps: { danger: true },
      onOk: () => {
        setDraft(company);
        onEditingChange(false);
      },
    });
  };

  const handleLogoUpload: UploadProps['onChange'] = async ({ file }) => {
    if (file.status !== 'done' || !companyId || !file.originFileObj) return;

    setSaving(true);
    try {
      const uploaded = await CompanyService.PatchFile(
        companyId,
        file.originFileObj
      );
      onLogoChange(await fileToDataUrl(file.originFileObj));
      const updatedCompany = { ...company, companyFile: uploaded };
      onCompanyChange(updatedCompany);
      setDraft(updatedCompany);
      UtilService.Alert(
        'Logo updated',
        'success',
        'The company logo has been uploaded.'
      );
    } catch {
      UtilService.Alert(
        'Unable to upload logo',
        'error',
        'Check the image and try again.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleLogoRemove = async () => {
    if (!companyId || !company.companyFile?.id) return;

    setSaving(true);
    try {
      await CompanyService.DeleteFile(companyId, company.companyFile.id);
      onLogoChange('');
      const updatedCompany = { ...company, companyFile: undefined };
      onCompanyChange(updatedCompany);
      setDraft(updatedCompany);
      UtilService.Alert(
        'Logo removed',
        'success',
        'The company logo has been removed.'
      );
    } catch {
      UtilService.Alert(
        'Unable to remove logo',
        'error',
        'Try removing the logo again.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (editing && isAdmin) {
    return (
      <CompanyEditor
        company={draft}
        dirty={dirty}
        logo={logo}
        saving={saving}
        onCancel={handleCancel}
        onChange={handleChange}
        onLogoRemove={() => void handleLogoRemove()}
        onLogoUpload={handleLogoUpload}
        onSave={() => void handleSave()}
        validateLogo={validateLogo}
      />
    );
  }

  const ownerName =
    profile?.fullname?.trim() ||
    `${profile?.firstname ?? ''} ${profile?.lastname ?? ''}`.trim() ||
    'Company administrator';

  return (
    <CompanyOverview
      company={company}
      logo={logo}
      metrics={metrics}
      ownerName={ownerName}
    />
  );
};

export default CompanyProfile;
